/**
 * Servicio de caché local usando localforage
 * Permite funcionamiento offline básico cacheando respuestas de APIs
 */
import localforage from 'localforage';

// Configurar localforage
localforage.config({
  name: 'datacultivo',
  storeName: 'api_cache',
  description: 'Caché de datos para modo offline'
});

// Tiempos de expiración por tipo de datos (en milisegundos)
const CACHE_TTL = {
  meteo_stations: 24 * 60 * 60 * 1000,      // 24 horas - estaciones no cambian frecuentemente
  meteo_readings: 30 * 60 * 1000,           // 30 minutos - lecturas meteorológicas
  avisos: 6 * 60 * 60 * 1000,               // 6 horas - avisos fitosanitarios
  precios: 24 * 60 * 60 * 1000,             // 24 horas - precios diarios
  formacion: 24 * 60 * 60 * 1000,           // 24 horas - actividades formativas
  comercios: 7 * 24 * 60 * 60 * 1000,       // 7 días - comercios cambian poco
  geojson: 7 * 24 * 60 * 60 * 1000,         // 7 días - datos geográficos estáticos
  default: 60 * 60 * 1000,                   // 1 hora por defecto
};

/**
 * Genera una clave de caché basada en la URL
 */
function getCacheKey(url) {
  // Limpiar la URL para usarla como clave
  return url.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 200);
}

/**
 * Determina el TTL basado en el tipo de datos
 */
function getTTL(url) {
  if (url.includes('meteo/stations') && !url.includes('sensors') && !url.includes('readings')) {
    return CACHE_TTL.meteo_stations;
  }
  if (url.includes('meteo/readings') || url.includes('sensors')) {
    return CACHE_TTL.meteo_readings;
  }
  if (url.includes('avisos-fitosanitarios')) {
    return CACHE_TTL.avisos;
  }
  if (url.includes('precios-diarios')) {
    return CACHE_TTL.precios;
  }
  if (url.includes('actividades-formativas') || url.includes('formacion')) {
    return CACHE_TTL.formacion;
  }
  if (url.includes('comercios')) {
    return CACHE_TTL.comercios;
  }
  if (url.includes('.geojson') || url.includes('comarcas')) {
    return CACHE_TTL.geojson;
  }
  return CACHE_TTL.default;
}

/**
 * Guarda datos en caché con timestamp
 */
export async function setCache(url, data) {
  try {
    const key = getCacheKey(url);
    const cacheEntry = {
      data,
      timestamp: Date.now(),
      url: url.substring(0, 500), // Guardar URL original (truncada) para debug
    };
    await localforage.setItem(key, cacheEntry);
    return true;
  } catch (error) {
    console.warn('Error guardando en caché:', error);
    return false;
  }
}

/**
 * Obtiene datos del caché si no han expirado
 */
export async function getCache(url) {
  try {
    const key = getCacheKey(url);
    const cacheEntry = await localforage.getItem(key);
    
    if (!cacheEntry) {
      return null;
    }
    
    const ttl = getTTL(url);
    const isExpired = Date.now() - cacheEntry.timestamp > ttl;
    
    if (isExpired) {
      // Datos expirados, pero los devolvemos como fallback
      return { data: cacheEntry.data, expired: true };
    }
    
    return { data: cacheEntry.data, expired: false };
  } catch (error) {
    console.warn('Error leyendo caché:', error);
    return null;
  }
}

/**
 * Fetch con soporte de caché
 * - Intenta obtener datos frescos de la red
 * - Si falla, usa datos del caché (incluso expirados)
 * - Guarda respuestas exitosas en caché
 */
export async function fetchWithCache(url, options = {}) {
  const { forceRefresh = false, ...fetchOptions } = options;
  
  // Si no se fuerza refresh, intentar caché primero para respuesta rápida
  if (!forceRefresh) {
    const cached = await getCache(url);
    if (cached && !cached.expired) {
      return { 
        data: cached.data, 
        fromCache: true, 
        expired: false 
      };
    }
  }
  
  // Intentar obtener datos de la red
  try {
    const response = await fetch(url, {
      ...fetchOptions,
      headers: {
        'Accept': 'application/json',
        ...fetchOptions.headers,
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    
    const data = await response.json();
    
    // Guardar en caché (no bloquear la respuesta)
    setCache(url, data);
    
    return { 
      data, 
      fromCache: false, 
      expired: false 
    };
  } catch (networkError) {
    console.warn('Error de red, buscando en caché:', networkError.message);
    
    // Falló la red, intentar caché (incluso expirado)
    const cached = await getCache(url);
    if (cached) {
      return { 
        data: cached.data, 
        fromCache: true, 
        expired: cached.expired,
        networkError: networkError.message 
      };
    }
    
    // No hay caché disponible
    throw networkError;
  }
}

/**
 * Limpia entradas de caché expiradas
 */
export async function cleanExpiredCache() {
  try {
    const keys = await localforage.keys();
    let cleaned = 0;
    
    for (const key of keys) {
      const entry = await localforage.getItem(key);
      if (entry && entry.timestamp) {
        const maxTTL = Math.max(...Object.values(CACHE_TTL));
        const isVeryOld = Date.now() - entry.timestamp > maxTTL * 2;
        
        if (isVeryOld) {
          await localforage.removeItem(key);
          cleaned++;
        }
      }
    }
    
    console.log(`Caché limpiado: ${cleaned} entradas eliminadas`);
    return cleaned;
  } catch (error) {
    console.warn('Error limpiando caché:', error);
    return 0;
  }
}

/**
 * Obtiene estadísticas del caché
 */
export async function getCacheStats() {
  try {
    const keys = await localforage.keys();
    let totalSize = 0;
    let expiredCount = 0;
    let validCount = 0;
    
    for (const key of keys) {
      const entry = await localforage.getItem(key);
      if (entry) {
        const size = JSON.stringify(entry).length;
        totalSize += size;
        
        // Verificar si está expirado usando TTL máximo
        const maxTTL = Math.max(...Object.values(CACHE_TTL));
        if (Date.now() - entry.timestamp > maxTTL) {
          expiredCount++;
        } else {
          validCount++;
        }
      }
    }
    
    return {
      totalEntries: keys.length,
      validEntries: validCount,
      expiredEntries: expiredCount,
      approximateSize: `${(totalSize / 1024).toFixed(2)} KB`,
    };
  } catch (error) {
    console.warn('Error obteniendo estadísticas:', error);
    return null;
  }
}

/**
 * Limpia todo el caché
 */
export async function clearCache() {
  try {
    await localforage.clear();
    console.log('Caché limpiado completamente');
    return true;
  } catch (error) {
    console.warn('Error limpiando caché:', error);
    return false;
  }
}

export default {
  setCache,
  getCache,
  fetchWithCache,
  cleanExpiredCache,
  getCacheStats,
  clearCache,
};
